import { ProviderLogo, hasProviderLogo } from './logos'
import { Dialog } from '../../app/ui'
import type { ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { setSide, type Side } from '../../app/side'
import { rate, useSelectCompany } from '../../lib'
import { STATUS_LABEL, type DirectoryCard, type PartnerStatus } from './types'

// Stack tags as people read them (the directory's layer filter uses the same words).
export const LAYER_LABEL: Record<string, string> = {
  inference: 'Inference',
  sandboxes: 'Sandboxes / VMs',
  gpu: 'GPU',
  post_training: 'Post-training',
  other: 'Other',
}

export function Logo({ name, url, slug }: { name: string; url: string | null; slug?: string }) {
  // A logo the partner (or staff) uploaded, then the bundled mark for the providers we know, then a letter.
  if (url) return <img className="logo" src={url} alt="" />
  if (hasProviderLogo(slug)) return <span className="logo brand"><ProviderLogo slug={slug!} size={40} /></span>
  return (
    <span className="logo fallback" aria-hidden>
      {name.slice(0, 1).toUpperCase()}
    </span>
  )
}

export function StatusBadge({ status }: { status: PartnerStatus | null }) {
  if (!status) return null
  return (
    <span className={`status status-${status}`} data-testid="web-partner-status">
      {STATUS_LABEL[status]}
    </span>
  )
}

export function RateLine({ card }: { card: Pick<DirectoryCard, 'rate'> }) {
  return (
    <span data-testid="web-rate">
      {card.rate.kind === 'yours' ? 'Yours: ' : 'To start: '}<strong className="num">{rate(card.rate.rate)}</strong>
    </span>
  )
}

/** A small tile: logo, name, one rate line. The whole tile opens the pop-up. */
export function PartnerCard({ card, onOpen }: { card: DirectoryCard; onOpen: (c: DirectoryCard) => void }) {
  return (
    <button type="button" className="pcard" data-testid={`web-card-${card.slug}`} onClick={() => onOpen(card)}>
      {card.limited && <span className="tag limited" data-testid="web-limited">Limited</span>}
      {card.status && <span className={`pstatus status-${card.status}`} data-testid="web-partner-status">{STATUS_LABEL[card.status]}</span>}
      <Logo name={card.name} url={card.logo_url} slug={card.slug} />
      <span className="pname">{card.name}</span>
      <span className="prate num" data-testid="web-rate">{rate(card.rate.rate)}</span>
    </button>
  )
}

/** The pop-up over the list: logo, name, rate, and at most one button. It never changes the side by itself:
 *  on the partner or staff side only "Connect as <company>" does, and the switcher then shows it. */
export function PartnerDialog({ card, signedIn, side, company, onClose }: {
  card: DirectoryCard; signedIn: boolean; side: Side | null; company?: { id: string; name: string }; onClose: () => void
}) {
  const navigate = useNavigate()
  const selectCompany = useSelectCompany()
  const inApp = card.limited ? '/invoices' : `/directory/${card.id}`
  const testid = `web-open-${card.slug}`
  let button: ReactNode = null
  if (side === 'partner' || side === 'staff') {
    if (company) {
      button = (
        <button type="button" className="primary" data-testid={testid}
          onClick={() => { selectCompany(company.id); setSide('company'); navigate(inApp) }}>
          Connect as {company.name}
        </button>
      )
    }
  } else if (signedIn) {
    button = <Link className="button primary" to={inApp} data-testid={testid}>{card.limited ? 'Connect' : 'Open'}</Link>
  } else {
    const to = card.limited ? `/login?next=${encodeURIComponent(inApp)}` : `/directory/${card.slug}`
    button = <Link className="button primary" to={to} data-testid={testid}>{card.limited ? 'Connect' : 'See offer'}</Link>
  }
  return (
    <Dialog label={card.name} onClose={onClose} testid="web-dialog">
      <div className="row">
        <Logo name={card.name} url={card.logo_url} slug={card.slug} />
        <h2 style={{ margin: 0 }}>{card.name}</h2>
      </div>
      <p className="big-rate num" data-testid="web-dialog-rate">{rate(card.rate.rate)}</p>
      {card.limited && <p className="muted" style={{ margin: 0 }} data-testid="web-dialog-back-month">Includes last month's spend.</p>}
      {button}
    </Dialog>
  )
}
